import { PropsWithChildren, useMemo } from 'react';

import { Box } from '@twilio-paste/core/box';
import {
  FormControl,
  FormControlTwoColumn,
  FormSection,
  FormSectionHeading,
} from '@twilio-paste/core/form';
import { HelpText } from '@twilio-paste/core/help-text';
import { Input } from '@twilio-paste/core/input';
import { Label } from '@twilio-paste/core/label';
import { RadioGroup, Radio } from '@twilio-paste/core/radio-group';
import { Separator } from '@twilio-paste/core/separator';
import { Stack } from '@twilio-paste/core/stack';
import { TextArea } from '@twilio-paste/core/textarea';
import { useUIDSeed } from '@twilio-paste/core/uid-library';
import { Field, useFormikContext } from 'formik';
import { isValidPhoneNumber } from 'libphonenumber-js';
import * as Yup from 'yup';

import { appsConfig } from '../../../config';
import { AppModel, CreateAppParams, AppChannel, CommunicationType, SenderType } from '../../../modules/Apps';
import { Nullable } from '../../../utils';
import { InfoLabel } from '../../common/InfoLabel';

import { AppChannelsSection } from './AppChannelsSection';
import { CallbackUrlsSection } from './CallbackUrlsSection/CallbackUrlsSection';
import { EmailChannelSection } from './EmailChannelSection';
import { RegenerateAppSecretsSection } from './RegenerateAppSecretsSection';
import { TwilioAccountSection } from './TwilioAccountSection';

export enum TwilioAccountOption {
  Existing = 'existing',
  New = 'new',
}

export interface AppSettingsFormValues {
  appId?: string;
  name: string;
  code: string;
  defaultCountryCode: string;
  defaultSMSSender?: Nullable<string>;
  defaultEmailSender?: Nullable<string>;
  defaultSenderType?: SenderType;
  defaultVoiceNumber?: Nullable<string>;
  twilioAccountOption: TwilioAccountOption;
  twilioAccountId?: string;
  twilioAccountSid?: string;
  twilioAccountAuthToken?: string;
  channels?: AppChannel[];
  optInMessage?: Nullable<string>;
  optOutMessage?: Nullable<string>;
  helpMessage?: Nullable<string>;
  startMessage?: Nullable<string>;
  sendgridPromotionalAsmGroupId: number;
  sendgridTransactionalAsmGroupId: number;
  communicationType: CommunicationType;
  twilioSmsOutboundStatusCallbackUrl?: string;
  twilioSmsInboundStatusCallbackUrl?: string;
  twilioEventStreamSinkUrl?: string;
  sendgridEmailWebhookUrl?: string;
}

export const AppSettingsFormValuesToDto = (values: AppSettingsFormValues): CreateAppParams => {
  const {
    channels = [],
    twilioAccountId,
    sendgridPromotionalAsmGroupId,
    sendgridTransactionalAsmGroupId,
    ...params
  } = values;

  const appChannels = channels.map(channel => ({ appId: values.appId, channel }));

  const normalizeGroupId = (type: CommunicationType, value?: number) => {
    return value && [type, CommunicationType.Both].includes(values.communicationType) ? value : 0;
  };

  return {
    ...params,
    defaultEmailSender: values.defaultEmailSender || null,
    defaultSMSSender: values.defaultSMSSender || null,
    defaultVoiceNumber: values.defaultVoiceNumber || null,
    optInMessage: values.optInMessage || null,
    optOutMessage: values.optOutMessage || null,
    helpMessage: values.helpMessage || null,
    startMessage: values.startMessage || null,
    appChannels,
    twilioAccountId: twilioAccountId || undefined,
    sendgridPromotionalAsmGroupId: normalizeGroupId(
      CommunicationType.Marketing,
      sendgridPromotionalAsmGroupId,
    ),
    sendgridTransactionalAsmGroupId: normalizeGroupId(
      CommunicationType.Transactional,
      sendgridTransactionalAsmGroupId,
    ),
  };
};

export const AppModelToFormValues = (values: AppModel): AppSettingsFormValues => {
  const { sendgridPromotionalAsmGroupId, sendgridTransactionalAsmGroupId } = values;
  let communicationType = CommunicationType.Both;

  if (sendgridPromotionalAsmGroupId && !sendgridTransactionalAsmGroupId) {
    communicationType = CommunicationType.Marketing;
  }

  if (sendgridTransactionalAsmGroupId && !sendgridPromotionalAsmGroupId) {
    communicationType = CommunicationType.Transactional;
  }

  return {
    ...values,
    optInMessage: values.optInMessage || '',
    optOutMessage: values.optOutMessage || '',
    helpMessage: values.helpMessage || '',
    startMessage: values.startMessage || '',
    defaultSenderType: values.defaultSenderType || undefined,
    twilioAccountSid: '',
    twilioAccountAuthToken: '',
    twilioAccountOption: TwilioAccountOption.Existing,
    twilioAccountId: values.twilioAccountId || '',
    channels: values.appChannels?.map(({ channel }) => channel),
    communicationType,
    sendgridPromotionalAsmGroupId: values.sendgridPromotionalAsmGroupId || 0,
    sendgridTransactionalAsmGroupId: values.sendgridTransactionalAsmGroupId || 0,
  };
};

export const createAppSettingsFormValidationSchema = () => {
  const smsShortCodeValidaionArgs = ['channels', 'defaultSenderType'];
  const hasChannel = (channels: AppChannel[], channel: AppChannel) => channels?.includes(channel);
  const isSmsShortCode = (channels: AppChannel[], senderType: SenderType) => senderType === SenderType.ShortCode && hasChannel(channels, AppChannel.Sms)

  return Yup.object().shape({
    name: Yup.string().max(100, 'App name is too long').required('App name is required'),
    code: Yup.string().max(4, 'App code is too long').required('App code is required'),
    twilioAccountSid: Yup.string().when('twilioAccountOption', {
      is: TwilioAccountOption.New,
      then: schema =>
        schema
          .length(34, 'Twilio account SID must have 34 characters')
          .required('Twilio Account SID is required'),
    }),
    twilioAccountAuthToken: Yup.string().when('twilioAccountOption', {
      is: TwilioAccountOption.New,
      then: schema =>
        schema
          .max(32, 'Twilio account auth token is too long')
          .required('Twilio Account auth token is required'),
    }),
    twilioAccountId: Yup.string().when('twilioAccountOption', {
      is: TwilioAccountOption.Existing,
      then: schema =>
        schema.when('channels', {
          is: (value: AppChannel[]) =>
            hasChannel(value, AppChannel.Sms) || hasChannel(value, AppChannel.Voice),
          then: schema => schema.required('Twilio account is required.'),
          otherwise: schema => schema.nullable(),
        }),
    }),
    defaultSenderType: Yup.string().when('channels', {
      is: (value: AppChannel[]) => hasChannel(value, AppChannel.Sms),
      then: schema => schema.required('Default Sender Type is required'),
      otherwise: schema => schema.nullable(),
    }),
    defaultCountryCode: Yup.string()
      .max(2, 'Country code is too long')
      .required('Country code is required'),
    defaultSMSSender: Yup.string().when('channels', {
      is: (value: AppChannel[]) => hasChannel(value, AppChannel.Sms),
      then: schema =>
        schema
          .required('Default SMS Sender is required')
          .test('is-valid-phone', 'Invalid phone number or short code', value => {
            if (!value) {
              return false;
            }
            //Check if it's a valid phone number or a short code
            return isValidPhoneNumber(value) || /^[0-9]{5,6}$/.test(value);
          }),
      otherwise: schema => schema.nullable(),
    }),
    defaultEmailSender: Yup.string().when('channels', {
      is: (value: AppChannel[]) => hasChannel(value, AppChannel.Email),
      then: schema => schema.email('Invalid email').required('Default Email Sender is required'),
      otherwise: schema => schema.nullable(),
    }),
    defaultVoiceNumber: Yup.string().when('channels', {
      is: (value: AppChannel[]) => hasChannel(value, AppChannel.Voice),
      then: schema => schema.required('Default Voice Number is required'),
      otherwise: schema => schema.nullable(),
    }),
    channels: Yup.array().required().min(1, 'You must specify at least one channel'),
    optInMessage: Yup.string().when('channels', {
      is: (value: AppChannel[]) => hasChannel(value, AppChannel.Sms),
      then: schema =>
        schema.max(1600, 'Start message is too long').required('Start message is required'),
      otherwise: schema => schema.nullable(),
    }),
    optOutMessage: Yup.string().when(smsShortCodeValidaionArgs, {
      is: isSmsShortCode,
      then: schema =>
        schema.max(1600, 'Stop message is too long').required('Stop message is required'),
    }),
    helpMessage: Yup.string().when(smsShortCodeValidaionArgs, {
      is: isSmsShortCode,
      then: schema =>
        schema.max(1600, 'Help message is too long').required('Help message is required'),
    }),
    startMessage: Yup.string().when(smsShortCodeValidaionArgs, {
      is: isSmsShortCode,
      then: schema =>
        schema.max(1600, 'Start message is too long').required('Start message is required'),
    }),
    sendgridPromotionalAsmGroupId: Yup.number().when(['channels', 'communicationType'], {
      is: (channels: AppChannel[], communicationType: CommunicationType) =>
        hasChannel(channels, AppChannel.Email) &&
        [CommunicationType.Marketing, CommunicationType.Both].includes(communicationType),
      then: schema =>
        schema
          .required('Promotional Group ID is required')
          .min(1, 'Promotional Group ID is required'),
      otherwise: schema => schema.nullable(),
    }),
    sendgridTransactionalAsmGroupId: Yup.number().when(['channels', 'communicationType'], {
      is: (channels: AppChannel[], communicationType: CommunicationType) =>
        hasChannel(channels, AppChannel.Email) &&
        [CommunicationType.Transactional, CommunicationType.Both].includes(communicationType),
      then: schema =>
        schema
          .required('Transactional Group ID is required')
          .min(1, 'Transactional Group ID is required'),
      otherwise: schema => schema.nullable(),
    }),
  });
};

interface AppSettingsFormProps {
  readOnly?: boolean;
  isEditing?: boolean;
}

export function AppSettingsForm({ readOnly, isEditing }: AppSettingsFormProps) {
  const seed = useUIDSeed();
  const { touched, errors, values, initialValues, setFieldValue } =
    useFormikContext<AppSettingsFormValues>();

  const selectedChannels: Map<AppChannel, boolean> = useMemo(() => {
    const channelMap = new Map<AppChannel, boolean>();
    values?.channels?.forEach(channel => {
      channelMap.set(channel, true);
    });
    return channelMap;
  }, [values?.channels]);

  const twilioAccountReadOnly = useMemo(() => {
    return (
      readOnly ||
      (isEditing && !appsConfig.allowTwilioAccountChange && !!initialValues.twilioAccountId)
    );
  }, [isEditing, readOnly, initialValues.twilioAccountId]);

  const isLongCodeSelected = values.defaultSenderType === SenderType.LongCode;

  return (
    <Box maxWidth="600px">
      <Stack orientation="vertical" spacing="space60">
        <FormSection>
          <FormSectionHeading>App Details</FormSectionHeading>
          <FormControlTwoColumn>
            <FormControl>
              <Label htmlFor={seed('name')} required>
                App Name
              </Label>
              <Field
                name="name"
                as={Input}
                type="text"
                id={seed('name')}
                placeholder="App Name"
                hasError={touched.name && !!errors.name}
                readOnly={readOnly}
              />
              {touched.name && !!errors.name && <HelpText variant="error">{errors.name}</HelpText>}
            </FormControl>
            <FormControl>
              <Label htmlFor={seed('code')} required>
                App Code
              </Label>
              <Field
                name="code"
                as={Input}
                type="text"
                id={seed('code')}
                placeholder="App Code"
                maxLength={4}
                hasError={touched.code && !!errors.code}
                readOnly={readOnly || isEditing}
              />
              {touched.code && !!errors.code && <HelpText variant="error">{errors.code}</HelpText>}
            </FormControl>
          </FormControlTwoColumn>
          <AppChannelsSection readOnly={readOnly} />
          <ConditionalChannelField
            allowedChannels={[AppChannel.Sms, AppChannel.Voice]}
            selectedChannels={selectedChannels}
          >
            <TwilioAccountSection readOnly={twilioAccountReadOnly} />
          </ConditionalChannelField>
        </FormSection>
        <Separator orientation="horizontal" />
        <FormSection>
          <FormSectionHeading>App Configuration</FormSectionHeading>
          <ConditionalChannelField
            allowedChannels={[AppChannel.Sms, AppChannel.Voice]}
            selectedChannels={selectedChannels}
          >
            <FormControlTwoColumn>
              <FormControl>
                <InfoLabel
                  htmlFor={seed('defaultCountryCode')}
                  infoText="Please provide the default ISO country code that will be used to prefix phone numbers not using E.164 format."
                  required
                >
                  Default Country Code
                </InfoLabel>
                <Field
                  name="defaultCountryCode"
                  as={Input}
                  type="text"
                  id={seed('defaultCountryCode')}
                  placeholder="Country Code"
                  hasError={touched.defaultCountryCode && !!errors.defaultCountryCode}
                  readOnly={readOnly}
                />
                {touched.defaultCountryCode && !!errors.defaultCountryCode && (
                  <HelpText variant="error">{errors.defaultCountryCode}</HelpText>
                )}
              </FormControl>
              <ConditionalChannelField
                allowedChannels={[AppChannel.Sms]}
                selectedChannels={selectedChannels}
              >
                <FormControl>
                  <InfoLabel
                    htmlFor={seed('defaultSMSSender')}
                    infoText="Please provide the default phone number in E.164 format or short code when sending messages without a defined sender."
                    required
                  >
                    Default SMS Sender
                  </InfoLabel>

                  <Field
                    name="defaultSMSSender"
                    as={Input}
                    type="text"
                    id={seed('defaultSMSSender')}
                    placeholder="Default Sms Sender"
                    hasError={touched.defaultSMSSender && !!errors.defaultSMSSender}
                    readOnly={readOnly}
                  />
                  {touched.defaultSMSSender && !!errors.defaultSMSSender && (
                    <HelpText variant="error">{errors.defaultSMSSender}</HelpText>
                  )}
                </FormControl>
              </ConditionalChannelField>
            </FormControlTwoColumn>
          </ConditionalChannelField>
          <ConditionalChannelField
            allowedChannels={[AppChannel.Sms]}
            selectedChannels={selectedChannels}
          >
              <Box display="flex" flexDirection="column" rowGap="space60">
                <FormControl>
                  <RadioGroup
                    name="defaultSenderType"
                    legend="Default Sender Type"
                    value={values.defaultSenderType}
                    onChange={value => setFieldValue('defaultSenderType', value)}
                  >
                    <Radio id={seed('shortCode')} value={SenderType.ShortCode} disabled={readOnly}>
                      Short Code
                    </Radio>
                    <Radio id={seed('longCode')} value={SenderType.LongCode} disabled={readOnly}>
                      <InfoLabel
                        htmlFor={seed('longCode')}
                        infoText="To configure the Start, Stop, and Help messages for long codes, configure the advanced opt-in options on the Twilio messaging service."
                      >
                        Long Code
                      </InfoLabel>
                    </Radio>
                  </RadioGroup>
                </FormControl>
                <FormControl>
                  <InfoLabel
                    htmlFor={seed('optInMessage')}
                    infoText="Please provide the auto-responder message customers will receive when they opt in to receive messages from this app."
                    required
                  >
                    Auto-responder Message
                  </InfoLabel>

                  <Field
                    name="optInMessage"
                    as={TextArea}
                    maxLength={1600}
                    type="text"
                    id={seed('optInMessage')}
                    placeholder="Auto-responder Message"
                    hasError={touched.optInMessage && !!errors.optInMessage}
                    disabled={readOnly}
                  />
                  {touched.optInMessage && !!errors.optInMessage && (
                    <HelpText variant="error">{errors.optInMessage}</HelpText>
                  )}
                </FormControl>
              {!isLongCodeSelected && (
                <>
                  <FormControl>
                    <InfoLabel
                      htmlFor={seed('optOutMessage')}
                      infoText="Please provide the opt-out/stop message customers will receive when they opt out of receiving messages from this app. "
                      required
                    >
                      Stop Message
                    </InfoLabel>
                    <Field
                      name="optOutMessage"
                      as={TextArea}
                      maxLength={1600}
                      type="text"
                      id={seed('optOutMessage')}
                      placeholder="Stop Message"
                      hasError={touched.optOutMessage && !!errors.optOutMessage}
                      disabled={readOnly}
                    />
                    {touched.optOutMessage && !!errors.optOutMessage && (
                      <HelpText variant="error">{errors.optOutMessage}</HelpText>
                    )}
                  </FormControl>

                  <FormControl>
                    <InfoLabel
                      htmlFor={seed('helpMessage')}
                      infoText="Please provide the help message customers will receive when they send HELP to the phone number or short code for this app."
                      required
                    >
                      Help Message
                    </InfoLabel>
                    <Field
                      name="helpMessage"
                      as={TextArea}
                      maxLength={1600}
                      type="text"
                      id={seed('helpMessage')}
                      placeholder="Help Message"
                      hasError={touched.helpMessage && !!errors.helpMessage}
                      disabled={readOnly}
                    />
                    {touched.helpMessage && !!errors.helpMessage && (
                      <HelpText variant="error">{errors.helpMessage}</HelpText>
                    )}
                  </FormControl>

                  <FormControl>
                    <InfoLabel
                      htmlFor={seed('startMessage')}
                      infoText="Please provide the start message customers will receive when they send START to the phone number or short code for this app."
                      required
                    >
                      Start Message
                    </InfoLabel>
                    <Field
                      name="startMessage"
                      as={TextArea}
                      maxLength={1600}
                      type="text"
                      id={seed('startMessage')}
                      placeholder="Start Message"
                      hasError={touched.startMessage && !!errors.startMessage}
                      disabled={readOnly}
                    />
                    {touched.startMessage && !!errors.startMessage && (
                      <HelpText variant="error">{errors.startMessage}</HelpText>
                    )}
                  </FormControl>
                </>
              )}
              </Box>
          </ConditionalChannelField>
          <ConditionalChannelField
            allowedChannels={[AppChannel.Voice]}
            selectedChannels={selectedChannels}
          >
            <FormControl>
              <InfoLabel
                htmlFor={seed('defaultVoiceNumber')}
                infoText="If no phone number is provided, please provide the default phone number in E.164 format to use when making an outbound call without a defined phone number."
                required
              >
                Default Voice Number
              </InfoLabel>
              <Field
                name="defaultVoiceNumber"
                as={Input}
                type="email"
                id={seed('defaultVoiceNumber')}
                placeholder="Voice Number"
                hasError={touched.defaultVoiceNumber && !!errors.defaultVoiceNumber}
                readOnly={readOnly}
              />
              {touched.defaultVoiceNumber && !!errors.defaultVoiceNumber && (
                <HelpText variant="error">{errors.defaultVoiceNumber}</HelpText>
              )}
            </FormControl>
          </ConditionalChannelField>
          <ConditionalChannelField
            allowedChannels={[AppChannel.Email]}
            selectedChannels={selectedChannels}
          >
            <EmailChannelSection readOnly={readOnly} />
          </ConditionalChannelField>
        </FormSection>
        {values.appId && !readOnly && (
          <>
            <Separator orientation="horizontal" />
            <Box marginTop="space40">
              <RegenerateAppSecretsSection appId={values.appId} />
            </Box>
          </>
        )}
        <CallbackUrlsSection {...initialValues} />
      </Stack>
    </Box>
  );
}

interface ConditionalChannelFieldProps {
  allowedChannels: AppChannel[];
  selectedChannels: Map<AppChannel, boolean>;
}

function ConditionalChannelField({
  allowedChannels,
  selectedChannels,
  children,
}: PropsWithChildren<ConditionalChannelFieldProps>) {
  const allowed = allowedChannels.some(channel => selectedChannels.get(channel));
  return allowed ? <>{children}</> : null;
}
